import React, { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import ImageWithPlaceholder from "../common/ImageWithPlaceholder";

const UpsellSuggestionModal = ({
  isOpen,
  onClose,
  suggestions = [],
  triggerItemName,
  triggerItemPrice = null,
  onConfirm,
  isAdding = false,
}) => {
  const [selectedItems, setSelectedItems] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setSelectedItems({});
  }, [isOpen]);

  const handleAdd = (item) => {
    if (!item?.id) return;
    setSelectedItems((prev) => ({
      ...prev,
      [item.id]: {
        item,
        qty: (prev[item.id]?.qty || 0) + 1,
      },
    }));
  };

  const handleRemove = (item) => {
    if (!item?.id) return;
    setSelectedItems((prev) => {
      const next = { ...prev };
      const currentQty = next[item.id]?.qty || 0;
      if (currentQty <= 1) {
        delete next[item.id];
        return next;
      }
      next[item.id] = {
        item,
        qty: currentQty - 1,
      };
      return next;
    });
  };

  const selectionList = useMemo(
    () =>
      Object.values(selectedItems)
        .filter(Boolean)
        .map((entry) => ({
          item: entry.item,
          qty: entry.qty,
          lineTotal: Number(entry.item?.basePrice || 0) * entry.qty,
        })),
    [selectedItems],
  );

  const selectionTotal = useMemo(() => {
    const addOnsTotal = selectionList.reduce(
      (sum, entry) => sum + entry.lineTotal,
      0,
    );
    const base = Number.isFinite(Number(triggerItemPrice))
      ? Number(triggerItemPrice)
      : 0;
    return base + addOnsTotal;
  }, [selectionList, triggerItemPrice]);

  const handleContinue = () => {
    if (selectionList.length === 0) {
      onConfirm?.([]);
      return;
    }
    onConfirm?.(selectionList);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Recommended Add-ons"
      size="md"
    >
      <div className="space-y-2.5">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-white via-indigo-50/50 to-white px-4 py-2.5 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                Recommended Pairing
              </p>
              <h3 className="mt-1 truncate text-lg font-black text-slate-900 dark:text-slate-100">
                {triggerItemName || "Suggested add-ons"}
              </h3>
            </div>
            {Number.isFinite(Number(triggerItemPrice)) && (
              <div className="shrink-0 rounded-xl bg-slate-900 px-3 py-1.5 text-right shadow-sm dark:bg-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300 dark:text-slate-500">
                  Base Price
                </p>
                <p className="mt-0.5 text-base font-black text-white dark:text-slate-900">
                  ${Number(triggerItemPrice).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="max-h-64 space-y-2 overflow-y-auto pr-1 pos-scrollbar">
          {suggestions.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900/70"
            >
              <ImageWithPlaceholder
                src={item.imageUrl}
                alt={item.name}
                size="sm"
                className="h-14 w-14 rounded-lg object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                  {item.name}
                </p>
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  ${Number(item.basePrice || 0).toFixed(2)}
                </p>
              </div>
              {selectedItems[item.id]?.qty ? (
                <div className="flex items-center gap-1.5 rounded-xl bg-slate-50 px-2 py-1 dark:bg-slate-800">
                  <button
                    type="button"
                    className="h-8 w-8 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-700"
                    onClick={() => handleRemove(item)}
                    disabled={isAdding}
                    aria-label="Remove add-on"
                  >
                    -
                  </button>
                  <span className="min-w-[18px] text-center text-sm font-bold text-slate-900 dark:text-slate-100">
                    {selectedItems[item.id].qty}
                  </span>
                  <button
                    type="button"
                    className="h-8 w-8 rounded-lg border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-700"
                    onClick={() => handleAdd(item)}
                    disabled={isAdding}
                    aria-label="Add add-on"
                  >
                    +
                  </button>
                </div>
              ) : (
                <Button
                  size="sm"
                  className="h-8 rounded-xl px-3.5 text-xs font-bold"
                  onClick={() => handleAdd(item)}
                  disabled={isAdding}
                >
                  Add
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Selected Add-ons
          </div>
          <div className="space-y-1.5">
            {selectionList.length === 0 ? (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                No add-ons selected.
              </p>
            ) : (
              selectionList.map((entry) => (
                <div
                  key={entry.item.id}
                  className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-700 dark:bg-slate-800/80 dark:text-slate-200"
                >
                  <span className="truncate">
                    {entry.item.name} x{entry.qty}
                  </span>
                  <span>${entry.lineTotal.toFixed(2)}</span>
                </div>
              ))
            )}
            <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-base font-black dark:border-slate-700">
              <span>Total with item</span>
              <span>${selectionTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-200 pt-2.5 dark:border-slate-700">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleContinue}
            disabled={isAdding}
            className="rounded-xl px-4 py-1.5 text-sm font-bold"
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpsellSuggestionModal;
